import {selectArrayTernary} from '@coolcolduk/util';
import {Box, Typography} from '@mui/material';
import {forwardRef, useEffect, useMemo} from 'react';
import GraphBarChart from '../../atoms/graphBarChart/GraphBarChart';
import GraphLineChart from '../../atoms/graphLineChart/GraphLineChart';
import GraphScatterChart from '../../atoms/graphScatterChart/GraphScatterChart';
import PebblePlotRenderer from '../../atoms/PebblePlotRenderer/PebblePlotRenderer';
import {parsePebblePlotJson} from '../../atoms/PebblePlotRenderer/utils/parsePebblePlotJson';
import {pebbleplotJsonSchema} from '../../atoms/PebblePlotRenderer/utils/pebbleplotSchema';
import {GraphType} from '../../types/GraphTypes';
import {parseGraphJsonString} from '../../utils/graphJsonParser';
import {getGraphRendererXDomain} from './utils/getGraphRendererXDomain';
import {getGraphRendererYDomain} from './utils/getGraphRendererYDomain';
import {mapGraphRendererConfig} from './utils/mapGraphRendererConfig';
import {transformGraphRendererData} from './utils/transformGraphRendererData';

export interface BondGraphRendererProps {
  /**
   * Raw JSON string to render. The renderer will try to render as a bond graph first,
   * otherwise fall back to pebbleplot.
   */
  graphJsonString: string | null | undefined;
  height?: number;
  /**
   * Receives an error message if parsing fails. Empty string means "no error".
   * Useful for editors (e.g. JSON input UI) so they don't need to parse separately.
   */
  onErrorMessage?: (message: string) => void;
}

/**
 * render graph based on graph json string
 * @param graphJsonString - The graph json string to render
 * @param height - The height of the graph
 * @returns
 */
const BondGraphRenderer = forwardRef<HTMLDivElement, BondGraphRendererProps>(
  ({graphJsonString, height = 300, onErrorMessage}, ref) => {
    const trimmedGraphJsonString = typeof graphJsonString === 'string' ? graphJsonString.trim() : '';

    const parseFromStringResult = useMemo(() => {
      if (!trimmedGraphJsonString) return null;
      return parseGraphJsonString(trimmedGraphJsonString);
    }, [trimmedGraphJsonString]);

    const resolvedGraphConfig = useMemo(() => {
      if (parseFromStringResult?.success) return parseFromStringResult.config;
      return null;
    }, [parseFromStringResult]);

    const canRenderPebblePlot = useMemo(() => {
      if (!trimmedGraphJsonString) return false;
      if (parseFromStringResult?.success) return false;

      try {
        const parsed = parsePebblePlotJson(trimmedGraphJsonString);
        return pebbleplotJsonSchema.safeParse(parsed).success;
      } catch {
        return false;
      }
    }, [parseFromStringResult, trimmedGraphJsonString]);

    const errorMessage = useMemo(() => {
      if (!trimmedGraphJsonString) return '';
      if (resolvedGraphConfig) return '';
      if (canRenderPebblePlot) return '';
      return parseFromStringResult && !parseFromStringResult.success
        ? parseFromStringResult.error
        : 'Invalid graph JSON';
    }, [canRenderPebblePlot, parseFromStringResult, resolvedGraphConfig, trimmedGraphJsonString]);

    useEffect(() => {
      if (!onErrorMessage) return;
      onErrorMessage(errorMessage);
    }, [errorMessage, onErrorMessage]);

    // These hooks must be called unconditionally (before any early returns)
    const parsedConfig = useMemo(() => mapGraphRendererConfig(resolvedGraphConfig), [resolvedGraphConfig]);

    // Transform data for chart format (for line and bar charts)
    const chartData = useMemo(() => transformGraphRendererData(parsedConfig.dataSeries), [parsedConfig.dataSeries]);

    if (!trimmedGraphJsonString) {
      return null;
    }

    // If we can't parse bond graph and it's not pebbleplot, render nothing.
    if (!resolvedGraphConfig && !canRenderPebblePlot) {
      return null;
    }

    if (canRenderPebblePlot) {
      return (
        <Box ref={ref} sx={{width: '100%', minWidth: 0, mb: 2}} data-testid="bond-graph-renderer">
          <PebblePlotRenderer jsonString={trimmedGraphJsonString} />
        </Box>
      );
    }

    if (
      !resolvedGraphConfig ||
      parsedConfig.dataSeries.length === 0 ||
      parsedConfig.dataSeries.every((series) => series.data.length === 0)
    ) {
      return (
        <Box
          sx={{
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'grey.300',
            borderRadius: 1,
          }}
          data-testid="bond-graph-renderer"
        >
          <Typography variant="body2" color="text.secondary">
            No graph data available
          </Typography>
        </Box>
      );
    }

    const colors = ['#1B263B', '#FF6F59', '#90BE6D', '#4A90E2', '#F5A623', '#9013FE'];
    const {chartType, xAxis} = parsedConfig;

    const xLabel = xAxis?.label || 'X Axis';
    const xDomain = getGraphRendererXDomain(xAxis);

    // Ensure height is a valid number
    const chartHeight = typeof height === 'number' && height > 0 ? height : 300;

    const chartComponent = selectArrayTernary(
      [chartType === GraphType.LINE, chartType === GraphType.BAR],
      [
        <GraphLineChart
          key="line"
          chartData={chartData}
          parsedConfig={parsedConfig}
          colors={colors}
          xLabel={xLabel}
          xDomain={xDomain}
          getYDomain={getGraphRendererYDomain}
        />,
        <GraphBarChart
          key="bar"
          chartData={chartData}
          parsedConfig={parsedConfig}
          colors={colors}
          xLabel={xLabel}
          getYDomain={getGraphRendererYDomain}
        />,
      ],
      <GraphScatterChart key="scatter" parsedConfig={parsedConfig} colors={colors} xLabel={xLabel} xDomain={xDomain} />,
    );

    return (
      <Box
        ref={ref}
        sx={{width: '100%', height: chartHeight, minHeight: chartHeight, minWidth: 0, mb: 2}}
        data-testid="bond-graph-renderer"
      >
        {chartComponent}
      </Box>
    );
  },
);

BondGraphRenderer.displayName = 'BondGraphRenderer';

export default BondGraphRenderer;
